import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';

import { empty, isLoggedIn } from './helpers';
import { Individual } from './model/individual';
import { WizardStep } from './wizard-step';
import { WizardService } from './wizard/wizard.service';

@Injectable({
  providedIn: 'root'
})
export class WizardGuardService implements CanActivate, CanActivateChild {

  private noSkip: boolean = false;

  constructor(
    private router: Router,
    private wizard: WizardService,
    @Inject(DOCUMENT) private doc: Document
  ) { }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> | Promise<boolean> {
    const individual: Individual = this.wizard.getIndividual();
    const component: WizardStep = <any>route.component || route.firstChild.component;
    const loggedIn = isLoggedIn(individual);

    const loginRedirect = () => {
      if (empty(individual.ind_badge_name)) {
        this.router.navigate(['/signup/contact'], { queryParamsHandling: 'preserve' });
      } else if (empty(individual.cxa_key) || (empty(individual.adr_line1) && requiresAddress(this.wizard))) {
        this.router.navigate(['/signup/address'], { queryParamsHandling: 'preserve' });
      } else if (route.paramMap.has('destination')) {
        const destination = route.paramMap.get('destination');
        this.router.navigate([destination]);
      } else if (route.queryParamMap.has('destination')) {
        const destination = route.queryParamMap.get('destination');
        if (destination.startsWith('/')) {
          window.location.href = destination;
        } else {
          window.location.href = '/' + destination;
        }
      } else if (this.wizard.isAffinity) {
        this.router.navigate(['/signup/AffinityPortal', this.wizard.Affinity, 'select']);
      } else {
        this.router.navigate(['/signup/selection'], { queryParamsHandling: 'preserve' });
      }

      if (this.wizard.HasReferral) {
        this.wizard.GetReferral(this.wizard.Referral).subscribe(result => this.wizard.setReferral(result));
      }
    };

    if (route.data && 'noSkip' in route.data) {
      this.noSkip = !!route.data.noSkip;
    }

    if (route.paramMap.has('affinity')) {
      this.wizard.Affinity = parseInt(route.paramMap.get('affinity'), 10);
    } else if (route.queryParamMap.has('affinity')) {
      this.wizard.Affinity = parseInt(route.queryParamMap.get('affinity'), 10);
    }

    if (route.queryParamMap.has('massageenvy')) {
      this.wizard.isTBS = route.queryParamMap.get('massageenvy') === 'tbs';
    }

    if (route.queryParamMap.has('referrer')) {
      this.wizard.Referral = parseInt(route.queryParamMap.get('referrer'), 10);
    }

    if (route.queryParamMap.has('destination')) {
      var destination = new URL(route.queryParamMap.get('destination'), this.doc.location.origin);
      this.wizard.Destination = destination;
    }

    if (route.queryParamMap.has('promo')) {
      this.wizard.Promo = route.queryParamMap.get('promo');
      let promoExpire = (new Date()).getTime() + 3600000;
      sessionStorage.setItem('wizardpromo', this.wizard.Promo + '|' + promoExpire);
    } else if (sessionStorage.getItem('wizardpromo')) {
      let promoDeets = sessionStorage.getItem('wizardpromo').split('|');
      if (Number(promoDeets[1]) > (new Date()).getTime()) {
        this.wizard.Promo = promoDeets[0];
      }
    }

    if (route.queryParamMap.has('prospecttype')) {
      this.wizard.ProspectType = route.queryParamMap.get('prospecttype');
    }

    if (component.Name) {
      switch (component.Name) {
        case 'EmailComponent':
          if (loggedIn) {
            loginRedirect();

            return false;
          } else {
            return true;
          }
        case 'LoginComponent':
          if (loggedIn) {
            loginRedirect();

            return false;
          } else {
            return true;
          }
        case 'ContactInfoComponent':
          return true;
        case 'AddressComponent':
        case 'IndividualAffiliationsComponent':
        case 'AssociationLevelComponent':
        case 'CheckoutComponent':
          if (loggedIn) {
            return true;
          } else {
            this.router.navigate(['/signup/email'], { queryParamsHandling: 'preserve' });
            return false;
          }
        case 'MonthlyPaymentComponent':
        case 'AffinityPortalComponent':
          if (loggedIn) {
            if (empty(individual.cxa_key) || empty(individual.adr_line1)) {
              this.router.navigate(
                ['/signup/address'],
                {
                  queryParams: { 'affinity': this.wizard.Affinity },
                  queryParamsHandling: 'merge'
                }
              );
              return false;
            } else if (this.wizard.isAffinity) {
              return true;
            } else {
              return false;
            }
          } else {
            this.router.navigate(['/signup/email'], { queryParamsHandling: 'preserve' });
            return false;
          }
        default:
          return true;
      }
    } else {
      return true;
    }
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> | Promise<boolean> {
    if (childRoute.parent.paramMap.has('affinity')) {
      this.wizard.Affinity = parseInt(childRoute.parent.paramMap.get('affinity'), 10);
    }

    if (childRoute.queryParamMap.has('massageenvy')) {
      this.wizard.isTBS = childRoute.queryParamMap.get('massageenvy') === 'tbs';
    }

    return true;
  }
}

export function requiresAddress(wizard: WizardService): boolean {
  if (wizard.HasProspectType) {
    return false;
  }

  return true;
};
